import React from 'react';
import _ from 'lodash';
import { useUiTheme } from 'framework/uiTheme';
import { DefaultDropdownProps, Option } from './dropdownHelper';
import useInputHelper from './inputHelper';
import { ResultResourceData } from 'framework/crud';
import { CommonInputLayoutProps, DefaultInputPropTypes } from './types';
import { StylesConfig } from 'react-select';

const DEFAULT_PLACEHOLDER = 'Select...';

type Suggestion = {
  id: string | number;
  name: string;
};

export interface MultiSelectInputLayoutProps extends CommonInputLayoutProps<object, Nullable<Option[]>> {
  onLoadOptions: (inputValue: object, callback: (options: Nullable<Option[]>) => void) => void;
  placeholder?: string;
  noOptionsMessage?: (value?: Dictionary<string>) => Nullable<string>;
  menuPortalTarget?: Nullable<HTMLElement>;
  isMulti?: boolean;
  styles?: StylesConfig<Option, boolean>;
  getOptionValue?: (option: Option) => string;
  defaultOptions?: boolean | Option[];
  formatOptionLabel?: NonNullable<DefaultDropdownProps['formatOptionLabel']>;
}

export type MultiSelectInputLayoutType = ReactComponent<MultiSelectInputLayoutProps>;

export interface MultiSelectInputProps extends DefaultInputPropTypes<Option[]> {
  loadSuggestionsMethod: (searchValue: object) => ResultResourceData;
  mapResults?: (data: Suggestion[]) => Option[];
  filterLoadedOptions?: (options: Option[], selectedOptions: Option[]) => Option[];
  debounceDelay?: number;
  noOptionsMessage?: (value?: Dictionary<string>) => string;
  menuPortalTarget?: Nullable<HTMLElement>;
  isMulti?: boolean;
  defaultOptions?: boolean | Option[];
  styles?: StylesConfig<Option, boolean>;
  getOptionValue?: (option: Option) => string;
  formatOptionLabel?: NonNullable<DefaultDropdownProps['formatOptionLabel']>;
}

const MultiSelectInput = ({
  loadSuggestionsMethod,
  mapResults,
  filterLoadedOptions,
  debounceDelay = 500,
  noOptionsMessage = () => 'Nothing was found',
  menuPortalTarget,
  isMulti,
  styles,
  getOptionValue,
  defaultOptions,
  formatOptionLabel,
  ...props
}: MultiSelectInputProps) => {
  const { MultiSelectInputLayout } = useUiTheme<MultiSelectInputLayoutType>();
  const inputHelper = useInputHelper(props);
  const currentValue = inputHelper.getValue();

  const resolveOptionId = (option: Option): string => {
    if (getOptionValue) {
      return getOptionValue(option);
    }

    if (typeof option.value === 'object') {
      return JSON.stringify(option.value);
    }

    return String(option.value);
  };

  const isOption = (item: unknown): item is Option =>
    !!item && typeof item === 'object' && 'value' in (item as Record<string, unknown>);

  const resolveAnyId = (item: unknown): string => {
    if (isOption(item)) {
      return resolveOptionId(item);
    }
    return String(item);
  };

  const selectedIds = new Set(
    Array.isArray(currentValue)
      ? currentValue.map((item: unknown) => resolveAnyId(item))
      : currentValue
        ? [resolveAnyId(currentValue)]
        : [],
  );

  const loadOptions = async (inputValue: object, callback: (options: Nullable<Option[]>) => void) => {
    try {
      const data = await loadSuggestionsMethod(inputValue);
      let result;

      if (mapResults) {
        result = mapResults(data);
      } else {
        result = data.map((item: Suggestion) => ({
          value: item.id,
          label: item.name,
        }));
      }

      const optionsWithoutSelected = (result || []).filter(
        (option: Option) => option.disabled || !selectedIds.has(resolveOptionId(option)),
      );

      const normalizedSelectedOptions: Option[] = Array.isArray(currentValue)
        ? currentValue.filter((item: unknown): item is Option => isOption(item))
        : isOption(currentValue)
          ? [currentValue as Option]
          : [];

      const filteredOptions = filterLoadedOptions
        ? filterLoadedOptions(optionsWithoutSelected, normalizedSelectedOptions)
        : optionsWithoutSelected;

      callback(filteredOptions);
    } catch {
      callback(null);
    }
  };

  const loadDebounced = _.debounce(loadOptions, debounceDelay);

  const handleLoadOptions = (inputValue: object, callback: (options: Nullable<Option[]>) => void) => {
    if (!inputValue) {
      callback(null);
    }

    loadDebounced(inputValue, callback);
  };
  let arrayOfObjects = currentValue;

  if (Array.isArray(arrayOfObjects)) {
    const uniqueObjects = arrayOfObjects.filter(
      (option: unknown, index: number, arr: unknown[]) =>
        index === arr.findIndex(item => resolveAnyId(item) === resolveAnyId(option)),
    );

    arrayOfObjects = uniqueObjects;
  }

  return (
    <MultiSelectInputLayout
      onLoadOptions={handleLoadOptions}
      noOptionsMessage={noOptionsMessage}
      isVisible={inputHelper.isVisible()}
      label={inputHelper.getLabel()}
      source={inputHelper.getSource()}
      onChangeCallback={inputHelper.getOnChangeCallback()}
      onBlurCallback={inputHelper.getValidationCallback()}
      onFocusCallback={inputHelper.getClearValidationCallback()}
      value={arrayOfObjects}
      errorMessages={inputHelper.getErrorMessages()}
      isRequired={inputHelper.getIsRequired()}
      placeholder={inputHelper.getPlaceholder() || DEFAULT_PLACEHOLDER}
      menuPortalTarget={menuPortalTarget}
      helpText={inputHelper.getHelpText()}
      className={inputHelper.getClassName()}
      isMulti={isMulti}
      styles={styles}
      getOptionValue={resolveOptionId}
      defaultOptions={defaultOptions}
      formatOptionLabel={formatOptionLabel}
    />
  );
};

export default MultiSelectInput;
